<script>
    import { onMount } from "svelte";
    import Spinner from "../components/Spinner.svelte";
	import { user, token } from '../stores.js';
	import { apiPost } from '../api.js';

	user.useLocalStorage();

	const base_url = window.location.origin;
	const url = "SECURESHARE_API_URL";
	const api_key = "SECURESHARE_API_KEY";

	const ID_DISPLAY_LENGTH = 15;

    let secrets = $state([]);
    let loading = $state(true);
    let error = $state(null);
	let copiedId = $state(null);

	function truncateId(id) {
		return id.length > ID_DISPLAY_LENGTH ? id.slice(0, ID_DISPLAY_LENGTH) + '…' : id;
	}

	async function copyReadUrl(id) {
		await navigator.clipboard.writeText(`${base_url}/read/${id}`);
		copiedId = id;
		setTimeout(() => { if (copiedId === id) copiedId = null; }, 2000);
	}

    async function requestMySecrets() {
		const res = await apiPost(`${url}/retrieve_my_secrets`, {}, { token: $token, apiKey: api_key });
		const json = await res.json();
		if (res.ok) {
			secrets = json.secrets;
		} else {
			error = "Failed to load messages.";
		}
		loading = false;
	}

	async function deleteSecret(id) {
		if (!confirm("Delete this message? This cannot be undone.")) return;

		const res = await apiPost(`${url}/delete_secret`, { id: id }, { token: $token, apiKey: api_key });
		if (res.ok) {
			secrets = secrets.filter(s => s.id !== id);
		}
	}

	function formatExpiry(ttl) {
		const date = new Date(ttl * 1000);
		return date.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
	}

	function isExpired(ttl) {
		return Date.now() > ttl * 1000;
	}

	// Fire the fetch as soon as $token is available, and re-fire if it
	// changes. Each time Home is navigated back to, this component is
	// remounted when $page changes, which re-runs the $effect with
	// fresh local state — so the table reloads on every nav back, not
	// just on the first ever mount. The earlier `fetched` guard was the
	// race-condition fix for Login.svelte's async token fetch; the
	// reactive trigger naturally handles that case too (it just fires
	// twice when the token lands instead of once).
	$effect(() => {
		if ($token) requestMySecrets();
	});
</script>

{#if loading}
	<Spinner></Spinner>
{:else if error}
	<p class="alert alert-danger">{error}</p>
{:else if secrets.length === 0}
	<p class="text-muted">No messages sent yet.</p>
{:else}
	<table class="table">
		<thead>
			<tr>
				<th>ID</th>
				<th>Memo</th>
				<th>Internal Note</th>
				<th>Recipient</th>
				<th>Expires</th>
				<th>Reads</th>
				<th>File</th>
				<th></th>
			</tr>
		</thead>
		<tbody>
		{#each secrets as secret}
			<tr class:expired={isExpired(secret.TTL)}>
				<td class="id-cell">
					<span class="id-text" title={secret.id}>{truncateId(secret.id)}</span>
					<button class="copy-btn" onclick={() => copyReadUrl(secret.id)} title="Copy link to clipboard">
						{#if copiedId === secret.id}
							<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/></svg>
						{:else}
							<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/><path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/></svg>
						{/if}
					</button>
				</td>
				<td class="memo-cell" title={secret.memo || ''}>{secret.memo || ''}</td>
				<td>{secret.note || ''}</td>
				<td>{secret.to_address || ''}</td>
				<td>{formatExpiry(secret.TTL)}</td>
				<td>{secret.read_count} / {secret.burn_after_reading}</td>
				<td>{secret.has_file ? 'Yes' : 'No'}</td>
				<td>
					<button onclick={() => deleteSecret(secret.id)} class="btn btn-sm btn-outline-danger">Delete</button>
				</td>
			</tr>
		{/each}
		</tbody>
	</table>
{/if}

<style>
	.expired td {
		opacity: 0.5;
	}

	.expired td a:not(.btn) {
		text-decoration: line-through;
	}

	table {
		font-size: 14px;
	}

	th, td {
		padding: 6px 10px;
	}

	.id-cell {
		white-space: nowrap;
	}

	.id-text {
		font-family: monospace;
		font-size: 13px;
	}

	.memo-cell {
		max-width: 240px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.copy-btn {
		background: none;
		border: none;
		padding: 0 .25rem;
		cursor: pointer;
		color: #6c757d;
		vertical-align: middle;
	}

	.copy-btn:hover {
		color: #0d6efd;
	}
</style>
